import {
  IGroupInfo,
  IGroupInfoMembers,
  searchUsersByName,
} from "@home-health-notify/domain";
import {
  Autocomplete,
  AutocompleteChangeReason,
  TextField,
} from "@mui/material";
import { UserDirectoryContext, useTranslation } from "hhn-commons";
import { useContext, useMemo, useState } from "react";
import GroupMessageSearchOption from "./GroupMessageSearchOption";
import SendersList from "./SendersList";

export type SenderType = {
  id: string;
  name: string;
};

type GroupMessageSearchInputProps = {
  groupInfo: IGroupInfo;
  selectedSenders: SenderType[];
  onChange: (value: SenderType[]) => void;
  onClearFilters: (value: SenderType[]) => void;
};

const GroupMessageSearchInput = ({
  groupInfo,
  selectedSenders,
  onChange,
  onClearFilters,
}: GroupMessageSearchInputProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const { userDirectory } = useContext(UserDirectoryContext);
  const [inputValue, setInputValue] = useState("");

  const userDetails: SenderType[] = useMemo(() => {
    const users =
      groupInfo.members?.map((member: IGroupInfoMembers) => {
        const user = userDirectory.getUserInfo(member.userId);
        return {
          id: member.userId,
          name: user.title,
        };
      }) || [];

    return users.sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupInfo.members]);

  const sortedOptions = useMemo(() => {
    if (!inputValue) {
      return userDetails;
    }
    const usersWithFullInfo = userDetails.map(user => ({
      ...userDirectory.getUserInfo(user.id),
    }));
    const sortedUsers = searchUsersByName(inputValue, usersWithFullInfo);
    return sortedUsers
      .map(user => userDetails.find(option => option.id === user.id))
      .filter((option): option is SenderType => option !== undefined);
  }, [inputValue, userDetails, userDirectory]);

  return (
    <Autocomplete
      multiple
      options={sortedOptions}
      data-testid="FilterBox"
      disableCloseOnSelect
      getOptionLabel={userDetail => userDetail.name}
      getOptionKey={userDetail => userDetail.id}
      value={selectedSenders}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        // Don't clear input when selecting or removing options
        if (
          reason === "reset" ||
          reason === "selectOption" ||
          reason === "removeOption"
        ) {
          return;
        }
        setInputValue(newInputValue);
      }}
      onChange={(event, selectedSenders, reason: AutocompleteChangeReason) => {
        if (reason === "clear") {
          onClearFilters(selectedSenders);
        } else onChange(selectedSenders);
      }}
      isOptionEqualToValue={(option, selectedSender) =>
        option.id === selectedSender.id
      }
      renderValue={selectedSenders => {
        return <SendersList senders={selectedSenders} />;
      }}
      renderOption={({ key, ...props }, userDetail, { selected }) => (
        <GroupMessageSearchOption
          userDetail={userDetail}
          selected={selected}
          key={key}
          {...props}
        />
      )}
      limitTags={1}
      sx={{
        minWidth: "150px",
        width: "300px",
        flex: "0 1 300px",
      }}
      renderInput={params => (
        <TextField
          variant="standard"
          {...params}
          placeholder={tNs("search.filterPlaceholder")}
        />
      )}
    />
  );
};

export default GroupMessageSearchInput;
